import {
    Box,
    Button,
    Caption,
    Card,
    EmptyState,
    FilterIcon,
    Heading,
    NavArrowRight,
    Report,
    StyledIcon,
    useSubPage,
} from "@bcc-ui/ui-components";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import { AiBanner } from "./cards/ai-banner/AiBanner";
import { TotalImportedAssetsCard } from "./cards/total-imported-assets-card/TotalImportedAssetsCard";
import { MonitorWithGear } from "./MonitorWithGear";
import style from "./overview-tab.scss";
import { useAIPanelContext } from "components/ai-panel/AIPanelContext";
import { CreateAssetCustomViewSubpage } from "components/asset-management-system/custom-views/CreateAssetCustomViewSubpage";
import { RiskDistributionCard } from "components/asset-management-system/custom-views/tabs/risk/cards/risk-distribution-card/RiskDistributionCard";
import { ManageAssetView } from "components/asset-management-system/manage-assets/ManageAssetView";
import { UploadAssetsModal } from "components/asset-management-system/upload/UploadAssetsModal";
import ErrorMessage from "components/error-message/ErrorMessage";
import { subpageContext } from "components/layout/ApplicationLayout";
import { LoadingIndicator } from "components/loading-indicator/LoadingIndicator";
import { RiskLevel } from "domain/asset-management/assetFilterConstants";
import { AssetCustomView } from "domain/asset-management/assets";
import { AssetImportJob, assetImportJobService } from "services/asset-management/AssetImportJobService";
import { AssetOverview, AssetSearchRequest, assetService, RiskSummary } from "services/asset-management/AssetService";
import { StoreState } from "store";
import { logger } from "utils/logging";

import testIds from "testIds.json";

interface Props {
    onViewDevices: (riskLevel?: RiskLevel) => void;
    assetSearchRequest: AssetSearchRequest;
    viewDetails?: AssetCustomView;
    refreshKey?: number;
}

const JOB_STATUS_POLL_INTERVAL_MILLISECONDS = 10_000;

export const OverviewTab = (props: Props) => {
    const { t } = useTranslation();
    const assetViews = useSelector((state: StoreState) => state.assetViewsReducer.views);
    const context = React.useContext(subpageContext);
    const { isEnabled: isAiPanelEnabled } = useAIPanelContext();
    const [riskSummaryData, setRiskSummaryData] = useState<RiskSummary | null>(null);
    const [assetOverviewData, setAssetOverviewData] = useState<AssetOverview | null>(null);
    const [groupJobsData, setGroupJobsData] = useState<AssetImportJob[] | null>(null);

    const [isError, setIsError] = useState<boolean>(false);
    const [isImportAssetModalOpen, setIsImportAssetModalOpen] = useState(false);
    const { setSubPage } = useSubPage();

    const promptCards = [
        {
            icon: <FilterIcon />,
            heading: t("AssetManagement.overview.emptyState.firstPrompt"),
            onClick: () => {
                context?.setSubpage({
                    content: (
                        <CreateAssetCustomViewSubpage
                            viewDetails={props.viewDetails}
                            parentUuid={props.viewDetails?.parentUuid}
                            folders={(assetViews ?? []).filter((view) => view.folder)}
                            openFileToFilterTab
                        />
                    ),
                });
            },
        },
        {
            icon: <Report />,
            heading: t("AssetManagement.overview.emptyState.secondPrompt"),
            onClick: () => setIsImportAssetModalOpen(true),
        },
        {
            icon: <MonitorWithGear />,
            heading: t("AssetManagement.overview.emptyState.thirdPrompt"),
            onClick: () => setSubPage(<ManageAssetView groupUuid={props.viewDetails?.uuid} />),
        },
    ];

    // The parent builds a new request object on every render. Compare by content to avoid refetching when nothing changed.
    const assetSearchRequestKey = JSON.stringify(props.assetSearchRequest);

    useEffect(() => {
        const abortController = new AbortController();

        setRiskSummaryData(null);
        setAssetOverviewData(null);
        setIsError(false);

        Promise.all([
            assetService.fetchRiskSummary(props.assetSearchRequest, abortController),
            assetService.fetchAssetOverview(props.assetSearchRequest, abortController),
        ])
            .then(([riskSummary, assetOverview]) => {
                setRiskSummaryData(riskSummary);
                setAssetOverviewData(assetOverview);
            })
            .catch((error: Error) => {
                if (!abortController.signal.aborted) {
                    setIsError(true);
                    logger.error("Failed to fetch asset overview view data: ", error);
                }
            });

        return () => abortController.abort();
    }, [assetSearchRequestKey, props.refreshKey]);

    useEffect(() => {
        const groupUuid = props.assetSearchRequest.groupUuid;

        if (!groupUuid || assetOverviewData == null || assetOverviewData.totalAssets === 0) {
            setGroupJobsData([]);
            return;
        }

        const abortController = new AbortController();

        const fetchGroupJobs = () => {
            assetImportJobService
                .fetchJobs(groupUuid, null, abortController)
                .then(setGroupJobsData)
                .catch((error: Error) => {
                    if (!abortController.signal.aborted) {
                        logger.error("Failed to fetch asset import jobs: ", error);
                    }
                });
        };

        fetchGroupJobs();
        const intervalId = setInterval(fetchGroupJobs, JOB_STATUS_POLL_INTERVAL_MILLISECONDS);

        return () => {
            clearInterval(intervalId);
            abortController.abort();
        };
    }, [props.assetSearchRequest.groupUuid, assetOverviewData]);

    if (isError) {
        return <ErrorMessage />;
    }

    if (riskSummaryData == null || assetOverviewData == null || groupJobsData == null) {
        return <LoadingIndicator />;
    }

    if (assetOverviewData.totalAssets === 0) {
        return (
            <>
                <Box columnLayout verticalAlignment="Center" horizontalAlignment="Center" className={style.emptyState}>
                    <EmptyState
                        text={<Caption greyTextStyle>{t("AssetManagement.overview.emptyState.caption")}</Caption>}
                    />
                    <Box columnLayout marginBottom={"Lg"} verticalAlignment={"Center"}>
                        <Heading variant={"h1"} className={style.heading}>
                            {t("AssetManagement.overview.emptyState.heading")}
                        </Heading>
                        <Heading variant={"SUBTITLE_1"} className={style.emptyStateContent} disableBottomSpacing>
                            {t("AssetManagement.overview.emptyState.content")}
                        </Heading>
                    </Box>
                    <Box columnLayout gap={"Sm"}>
                        {promptCards.map((card, index) => (
                            <Card key={index} className={style.promptCard} onClick={card.onClick}>
                                <Box justifyContent={"SpaceBetween"} verticalAlignment={"Center"}>
                                    <Box verticalAlignment={"Center"} gap={"Sm"}>
                                        <StyledIcon icon={card.icon} />
                                        <Heading variant={"SUBTITLE_2"}>{card.heading}</Heading>
                                    </Box>

                                    <Box verticalAlignment={"Center"} gap={"Xxxs"} className={style.startAction}>
                                        <Button variant={"Tertiary"}>
                                            {t("AssetManagement.overview.emptyState.start")}
                                            <NavArrowRight />
                                        </Button>
                                    </Box>
                                </Box>
                            </Card>
                        ))}
                    </Box>
                </Box>
                <UploadAssetsModal
                    isOpen={isImportAssetModalOpen}
                    onClose={() => setIsImportAssetModalOpen(false)}
                    groupUuid={props.viewDetails?.uuid}
                />
            </>
        );
    }

    return (
        <Box
            columnLayout
            gap={"Sm"}
            paddingBottom={"Sm"}
            className={style.layout}
            testId={testIds.workArea.asset.customView.overview.itself}
        >
            {isAiPanelEnabled && <AiBanner assetsTotalCount={assetOverviewData.totalAssets} />}

            <Box gap={"Sm"} className={style.cardsContainer}>
                <Box className={style.leftSection}>
                    <TotalImportedAssetsCard groupJobsData={groupJobsData} data={assetOverviewData} />
                </Box>

                <Box columnLayout gap={"Xs"} className={style.rightSection}>
                    <RiskDistributionCard data={riskSummaryData} />
                </Box>
            </Box>
        </Box>
    );
};
